import StrategyRefChip from "@/components/strategy/StrategyRefChip";
import StrategyStatusChip from "@/components/strategy/StrategyStatusChip";
import StrategyNameButton from "@/components/strategy/StrategyNameButton";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import PageHeader from "@/components/PageHeader";
import StatCard from "@/components/StatCard";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useAuth } from "@/lib/auth";
import { Navigate, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { ShieldAlert, Activity, Users, Plug, ScrollText, RefreshCw, Database, ToggleLeft, UserCheck, TrendingUp, Search, Settings2, Power, PowerOff, KeyRound, RotateCcw, Globe, CreditCard, Bot, Share2, Save, BookOpen, Download } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

function FeatureFlagsPanel() {
  const [flags, setFlags] = useState(null);
  const [saving, setSaving] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/features/all");
      setFlags(r.data.flags);
    } catch { toast.error("Could not load feature flags"); }
  };

  useEffect(() => { load(); }, []);

  const toggle = async (flag, currentCfg) => {
    setSaving(flag);
    try {
      await api.put(`/features/${flag}`, { enabled: !currentCfg.enabled });
      setFlags((prev) => ({ ...prev, [flag]: { ...currentCfg, enabled: !currentCfg.enabled } }));
      toast.success(`${flag} ${!currentCfg.enabled ? "enabled" : "disabled"}`);
    } catch { toast.error("Save failed"); }
    finally { setSaving(null); }
  };

  if (!flags) return <div className="txt-muted text-sm p-6">Loading…</div>;

  return (
    <div className="panel p-6 space-y-4">
      <div className="flex items-center gap-2 mb-4">
        <ToggleLeft className="w-4 h-4 txt-warn" />
        <div className="overline">Feature Flags</div>
        <span className="text-xs txt-muted ml-2">Toggle features per role. Changes are live immediately.</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {Object.entries(flags).map(([flag, cfg]) => (
          <div key={flag} className="flex items-center justify-between p-3 border border-[var(--border)] bg-[var(--bg-page)]">
            <div>
              <div className="text-sm font-section tracking-wider">{flag.replace(/_/g, " ").toUpperCase()}</div>
              <div className="text-xs txt-muted mt-0.5">{(cfg.roles || []).join(", ")}</div>
            </div>
            <Switch
              checked={!!cfg.enabled}
              disabled={saving === flag}
              onCheckedChange={() => toggle(flag, cfg)}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

const fmt = (n, d = 2) =>
  (n === null || n === undefined || isNaN(n)) ? "—" : Number(n).toLocaleString("en-IN", { minimumFractionDigits: d, maximumFractionDigits: d });

export default function Admin() {
  const { user } = useAuth();
  const [health, setHealth] = useState(null);
  const [users, setUsers] = useState([]);
  const [brokerMap, setBrokerMap] = useState({ connections: [], by_broker: {} });
  const [events, setEvents] = useState([]);
  const [adminEvents, setAdminEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState(null);
  const [dataSaving, setDataSaving] = useState(false);
  const [dataForm, setDataForm] = useState({ default: "angelone", fallback: "dhan" });
  const [mcxDataSource, setMcxDataSource] = useState(null);
  const [mcxForm, setMcxForm] = useState({ default: "angelone", fallback: "dhan" });
  const [mcxSaving, setMcxSaving] = useState(false);
  const [tvConfig, setTvConfig] = useState(null);
  const [tvForm, setTvForm] = useState({ username: "", password: "" });
  const [tvSaving, setTvSaving] = useState(false);
  const [mcxInstruments, setMcxInstruments] = useState(null);
  const [mcxIdsForm, setMcxIdsForm] = useState({ GOLD: "", SILVER: "", CRUDEOIL: "", NATURALGAS: "", COPPER: "", ZINC: "" });
  const [mcxIdsSaving, setMcxIdsSaving] = useState(false);
  const [promotingId, setPromotingId] = useState(null);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [promotionReqs, setPromotionReqs] = useState([]);
  const [approvingId, setApprovingId] = useState(null);
  const [userSearch, setUserSearch] = useState("");
  const [platformCfg, setPlatformCfg] = useState({ scan_interval_minutes: 30, auto_paper_trap_signals: false, trap_auto_confidence_min: 0.70, strategy_prompt_max_length: 2000, require_approval: false });
  const [scanInterval, setScanInterval] = useState(30);
  const [autoPaperTrap, setAutoPaperTrap] = useState(false);
  const [trapConfMin, setTrapConfMin] = useState(70);
  const [stratPromptMax, setStratPromptMax] = useState(2000);
  const [requireApproval, setRequireApproval] = useState(false);
  const [savingCfg, setSavingCfg] = useState(false);

  // Platform-level config (payment, social, SEO, AI)
  const [extCfg, setExtCfg] = useState({
    razorpay_key_id: "", razorpay_key_secret: "",
    stripe_publishable_key: "", stripe_secret_key: "",
    twitter_url: "", linkedin_url: "", telegram_url: "", youtube_url: "", instagram_url: "",
    anthropic_api_key: "", openai_api_key: "",
    agent_model: "claude-sonnet-4-6", agent_temperature: 0.3, agent_max_tokens: 2048,
    seo_site_name: "AlgoForge", seo_tagline: "", seo_description: "",
    seo_og_image_url: "", google_analytics_id: "", google_search_console: "",
    smtp_host: "", smtp_port: 587, smtp_user: "", smtp_pass: "", smtp_from: "",
    support_email: "", app_url: "https://algoforge.tech",
  });
  const [loadingExtCfg, setLoadingExtCfg] = useState(false);
  const [savingExtCfg, setSavingExtCfg] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const [h, u, b, e, ae, ds, mcxDs, tvDs, pu, pr, pc, mcxInstr] = await Promise.all([
        api.get("/admin/health"),
        api.get("/admin/risk/users"),
        api.get("/admin/brokers/map"),
        api.get("/admin/audit", { params: { limit: 50 } }),
        api.get("/admin/events", { params: { limit: 50 } }),
        api.get("/admin/data-source"),
        api.get("/admin/data-source/mcx"),
        api.get("/admin/data-source/tradingview"),
        api.get("/admin/users/pending"),
        api.get("/admin/promotions"),
        api.get("/admin/platform-config"),
        api.get("/admin/mcx-instruments"),
      ]);
      setHealth(h.data);
      setUsers(u.data.items);
      setBrokerMap(b.data);
      setEvents(e.data.items);
      setAdminEvents(ae.data.items);
      setDataSource(ds.data);
      setDataForm({ default: ds.data.default, fallback: ds.data.fallback });
      setMcxDataSource(mcxDs.data);
      setMcxForm({ default: mcxDs.data.default, fallback: mcxDs.data.fallback });
      setTvConfig(tvDs.data);
      setTvForm({ username: tvDs.data.username || "", password: "" });
      setMcxInstruments(mcxInstr.data);
      const initIds = {};
      Object.entries(mcxInstr.data.ids || {}).forEach(([sym, info]) => {
        initIds[sym] = info.security_id !== "0" ? info.security_id : "";
      });
      setMcxIdsForm(f => ({ ...f, ...initIds }));
      setPendingUsers(pu.data.items);
      setPromotionReqs(pr.data.items);
      // Extended platform config
      try {
        setLoadingExtCfg(true);
        const ec = await api.get("/admin/platform-config/extended");
        setExtCfg((prev) => ({ ...prev, ...ec.data }));
      } catch {} finally { setLoadingExtCfg(false); }
      const cfg = pc.data;
      setPlatformCfg(cfg);
      setScanInterval(cfg.scan_interval_minutes ?? 30);
      setAutoPaperTrap(cfg.auto_paper_trap_signals ?? false);
      setRequireApproval(cfg.require_approval ?? false);
      setTrapConfMin(Math.round((cfg.trap_auto_confidence_min ?? 0.70) * 100));
      setStratPromptMax(cfg.strategy_prompt_max_length ?? 2000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Admin load failed");
    } finally {
      setLoading(false);
    }
  };

  const saveTvConfig = async () => {
    if (!tvForm.username.trim() || !tvForm.password.trim()) {
      toast.error("Username and password are required");
      return;
    }
    setTvSaving(true);
    try {
      await api.put("/admin/data-source/tradingview", tvForm);
      toast.success("TradingView credentials saved");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save TradingView config");
    } finally {
      setTvSaving(false);
    }
  };

  const saveMcxInstruments = async () => {
    const ids = {};
    Object.entries(mcxIdsForm).forEach(([sym, val]) => {
      if (val && val.trim()) ids[sym] = val.trim();
    });
    if (!Object.keys(ids).length) {
      toast.error("Enter at least one security ID");
      return;
    }
    setMcxIdsSaving(true);
    try {
      const { data } = await api.put("/admin/mcx-instruments", { ids });
      toast.success(`MCX IDs saved — ${data.live_count} symbols active`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save MCX instrument IDs");
    } finally {
      setMcxIdsSaving(false);
    }
  };

  const saveMcxDataSource = async () => {
    setMcxSaving(true);
    try {
      await api.put("/admin/data-source/mcx", mcxForm);
      toast.success("MCX data source saved");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save MCX data source");
    } finally {
      setMcxSaving(false);
    }
  };

  const saveDataSource = async () => {
    setDataSaving(true);
    try {
      await api.put("/admin/data-source", dataForm);
      toast.success("Data source configuration saved");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setDataSaving(false);
    }
  };

  useEffect(() => {
    if (user?.role === "admin") load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  if (user && user.role !== "admin") return <Navigate to="/" replace />;

  const promoteUser = async (email, newRole) => {
    setPromotingId(email);
    try {
      await api.post("/admin/promote", { target_email: email, role: newRole });
      toast.success(`${email} → ${newRole.toUpperCase()}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Promote failed");
    } finally {
      setPromotingId(null);
    }
  };

  const approveUser = async (userId, email) => {
    setApprovingId(userId);
    try {
      await api.post(`/admin/users/${userId}/approve`);
      toast.success(`${email} approved`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Approval failed");
    } finally {
      setApprovingId(null);
    }
  };

  const approvePromotion = async (reqId, userEmail) => {
    setApprovingId(reqId);
    try {
      await api.post(`/admin/promotions/${reqId}/approve`, { note: "" });
      toast.success(`${userEmail} promoted to Trader`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Approve failed");
    } finally {
      setApprovingId(null);
    }
  };

  const rejectPromotion = async (reqId) => {
    const note = window.prompt("Rejection reason (optional):");
    if (note === null) return;
    try {
      await api.post(`/admin/promotions/${reqId}/reject`, { note });
      toast.success("Promotion request rejected");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reject failed");
    }
  };

  const toggleUserStatus = async (userId, email, currentStatus) => {
    const next = currentStatus === "active" ? "suspended" : "active";
    try {
      if (next === "active") {
        await api.post(`/admin/users/${userId}/enable`);
      } else {
        await api.post(`/admin/users/${userId}/suspend`);
      }
      toast.success(`${email} → ${next}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Status toggle failed");
    }
  };

  const resetPassword = async (userId, email) => {
    if (!window.confirm(`Force reset password for ${email}? A new temporary password will be generated.`)) return;
    try {
      const { data } = await api.post("/admin/users/reset-password", { user_id: userId });
      toast.success(`Password reset for ${email}. Temp password: ${data.new_password}`, { duration: 15000 });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Password reset failed");
    }
  };

  const resetDemo = async (userId, email) => {
    if (!window.confirm(`Reset demo account for ${email}? All paper orders and positions will be cleared.`)) return;
    try {
      await api.post(`/admin/users/reset-demo/${userId}`);
      toast.success(`Demo account reset for ${email} — ₹10,00,000 restored`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Demo reset failed");
    }
  };

  const savePlatformConfig = async () => {
    setSavingCfg(true);
    try {
      await api.put("/admin/platform-config", {
        scan_interval_minutes:      Number(scanInterval),
        auto_paper_trap_signals:    autoPaperTrap,
        trap_auto_confidence_min:   trapConfMin / 100,
        strategy_prompt_max_length: stratPromptMax,
        require_approval:           requireApproval,
      });
      toast.success(`Platform config saved`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSavingCfg(false);
    }
  };

  const setUserTier = async (userId, email, tier) => {
    try {
      await api.post("/admin/users/set-tier", { user_id: userId, tier });
      toast.success(`${email} → ${tier}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Tier update failed");
    }
  };

  const saveExtCfg = async (section, fields) => {
    setSavingExtCfg(prev => ({ ...prev, [section]: true }));
    try {
      const payload = {};
      fields.forEach(f => { payload[f] = extCfg[f]; });
      await api.put("/admin/platform-config/extended", { section, ...payload });
      toast.success(`${section} settings saved`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSavingExtCfg(prev => ({ ...prev, [section]: false }));
    }
  };

  const forceKill = async (uid, email, killOn) => {
    const reason = window.prompt(`${killOn ? "Force-kill" : "Release kill on"} ${email}? Reason:`);
    if (reason === null) return;
    try {
      await api.post("/admin/risk/kill", { user_id: uid, kill_switch: killOn, reason });
      toast.success(`${killOn ? "Killed" : "Released"} ${email}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed");
    }
  };

  return (
    <AppShell>
      <PageHeader
        overline="Platform"
        title="Admin console"
        description="Super-admin view across all users. Every action here is recorded in the admin audit trail."
        actions={
          <Button data-testid="admin-refresh" onClick={load} disabled={loading} className="rounded-none bg-white text-black hover:bg-zinc-200 font-section tracking-wider">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> REFRESH
          </Button>
        }
      />

      <div className="p-8 space-y-6">
        {/* Health stats */}
        {health && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            <StatCard testid="admin-health-mongo" label="Mongo" value={health.mongo === "ok" ? "OK" : "ERR"} tone={health.mongo === "ok" ? "profit" : "loss"} />
            <StatCard testid="admin-health-reconciler" label="Reconciler" value={health.reconciler} tone="profit" />
            <StatCard
              testid="admin-health-llm"
              label="LLM Key"
              value={
                health.llm_key === "ok" || health.llm_key === "configured"
                  ? "OK"
                  : health.llm_key?.startsWith("unreachable") ? "FAIL"
                  : "MISSING"
              }
              sub={health.llm_ping?.via || health.llm?.effective_mode || ""}
              tone={
                health.llm_key === "ok" || health.llm_key === "configured"
                  ? "profit"
                  : "loss"
              }
            />
            <StatCard testid="admin-health-users" label="Users" value={health.users} />
            <StatCard testid="admin-health-orders" label="Paper orders" value={health.paper_orders} />
            <StatCard testid="admin-health-live" label="Live brokers" value={health.live_brokers} tone={health.live_brokers > 0 ? "profit" : "neutral"} />
          </div>
        )}

        {/* LLM error detail — visible when ping fails */}
        {health && health.llm_key !== "ok" && health.llm_key !== "configured" && (
          <div className="border border-red-500/30 bg-red-500/5 px-4 py-3 text-xs font-mono-data txt-loss space-y-1">
            <div className="font-section text-red-400 mb-1">LLM connectivity failure</div>
            <div>{health.llm_key}</div>
            {health.llm_ping?.error && <div className="txt-muted">{health.llm_ping.error}</div>}
            <div className="txt-muted mt-2">
              Fix: add <span className="text-white">ANTHROPIC_API_KEY=sk-ant-…</span> to{" "}
              <span className="text-white">backend/.env</span> then rebuild:{" "}
              <span className="text-white">docker compose build backend &amp;&amp; docker compose up -d backend</span>
            </div>
          </div>
        )}

        <Tabs defaultValue="risk" className="w-full">
          <TabsList className="rounded-none bg-[var(--bg-surface)] border border-[var(--border)] h-auto p-0 flex-wrap">
            <TabsTrigger value="risk" data-testid="admin-tab-risk" className="rounded-none data-[state=active]:bg-[var(--bg-surface-2)] data-[state=active]:text-white font-section tracking-wider px-6 py-3"><Users className="w-4 h-4 mr-2" /> RISK / USERS</TabsTrigger>
            <TabsTrigger value="datasource" data-testid="admin-tab-datasource" className="rounded-none data-[state=active]:bg-[var(--bg-surface-2)] data-[state=active]:text-white font-section tracking-wider px-6 py-3"><Database className="w-4 h-4 mr-2" /> DATA SOURCE</TabsTrigger>
            <TabsTrigger value="brokers" data-testid="admin-tab-brokers" className="rounded-none data-[state=active]:bg-[var(--bg-surface-2)] data-[state=active]:text-white font-section tracking-wider px-6 py-3"><Plug className="w-4 h-4 mr-2" /> BROKER MAP</TabsTrigger>
            <TabsTrigger value="audit" data-testid="admin-tab-audit" className="rounded-none data-[state=active]:bg-[var(--bg-surface-2)] data-[state=active]:text-white font-section tracking-wider px-6 py-3"><ScrollText className="w-4 h-4 mr-2" /> GLOBAL AUDIT</TabsTrigger>
            <TabsTrigger value="events" data-testid="admin-tab-events" className="rounded-none data-[state=active]:bg-[var(--bg-surface-2)] data-[state=active]:text-white font-section tracking-wider px-6 py-3"><ShieldAlert className="w-4 h-4 mr-2" /> ADMIN TRAIL</TabsTrigger>
            <TabsTrigger value="features" data-testid="admin-tab-features" className="rounded-none data-[state=active]:bg-[var(--bg-surface-2)] data-[state=active]:text-white font-section tracking-wider px-6 py-3"><Activity className="w-4 h-4 mr-2" /> FEATURE FLAGS</TabsTrigger>
            <TabsTrigger value="platform" data-testid="admin-tab-platform" className="rounded-none data-[state=active]:bg-[var(--bg-surface-2)] data-[state=active]:text-white font-section tracking-wider px-6 py-3"><Settings2 className="w-4 h-4 mr-2" /> PLATFORM</TabsTrigger>
            <TabsTrigger value="pending" data-testid="admin-tab-pending" className="rounded-none data-[state=active]:bg-[var(--bg-surface-2)] data-[state=active]:text-white font-section tracking-wider px-6 py-3 relative">
              <UserCheck className="w-4 h-4 mr-2" /> PENDING USERS
              {pendingUsers.length > 0 && <span className="ml-2 bg-yellow-500 text-black rounded-full w-5 h-5 text-xs flex items-center justify-center">{pendingUsers.length}</span>}
            </TabsTrigger>
            <TabsTrigger value="promotions" data-testid="admin-tab-promotions" className="rounded-none data-[state=active]:bg-[var(--bg-surface-2)] data-[state=active]:text-white font-section tracking-wider px-6 py-3">
              <TrendingUp className="w-4 h-4 mr-2" /> PROMOTIONS
              {promotionReqs.length > 0 && <span className="ml-2 bg-blue-500 text-white rounded-full w-5 h-5 text-xs flex items-center justify-center">{promotionReqs.length}</span>}
            </TabsTrigger>
            <TabsTrigger value="platform-settings" className="rounded-none data-[state=active]:bg-[var(--bg-surface-2)] data-[state=active]:text-white font-section tracking-wider px-6 py-3">
              <Globe className="w-4 h-4 mr-2" /> PLATFORM SETTINGS
            </TabsTrigger>
            <TabsTrigger value="strategies" data-testid="admin-tab-strategies" className="rounded-none data-[state=active]:bg-[var(--bg-surface-2)] data-[state=active]:text-white font-section tracking-wider px-6 py-3">
              <BookOpen className="w-4 h-4 mr-2" /> STRATEGIES
            </TabsTrigger>
          </TabsList>

          {/* Data Source */}
          <TabsContent value="datasource" className="mt-4">
            <div className="panel p-6 space-y-6 max-w-2xl">
              <div>
                <div className="font-display text-xl mb-1">Platform Market Data Source</div>
                <p className="text-sm txt-secondary">
                  Angel One is the primary market-data provider and Dhan the fallback (connect both under <strong>Brokers</strong>); they provide real-time and historical data for all platform users.
                  Traders only need their own trading broker — no separate data API required.
                </p>
              </div>

              {/* Current tier status */}
              {dataSource && (
                <div className="space-y-2">
                  <div className="overline mb-2">Active Data Tiers</div>
                  {[
                    { label: "Default", key: "default" },
                    { label: "Fallback", key: "fallback" },
                    { label: "Tier 3", broker: "yahoo", status: "always_available" },
                    { label: "Tier 4", broker: "mock",  status: "always_available" },
                  ].map((row, i) => {
                    const info = row.key
                      ? dataSource.status[row.key]
                      : { broker: row.broker, status: row.status };
                    const isLive = info?.status === "live" || info?.status === "always_available";
                    return (
                      <div key={i} className="flex items-center gap-4 border border-[var(--border)] px-4 py-3">
                        <span className={`w-2 h-2 rounded-full shrink-0 ${isLive ? "bg-emerald-500" : "bg-zinc-600"}`} />
                        <span className="overline w-20 shrink-0">{row.label}</span>
                        <span className="font-mono-data text-sm uppercase">{info?.broker || "—"}</span>
                        <span className={`text-xs ml-auto ${isLive ? "txt-profit" : "txt-muted"}`}>
                          {info?.status === "always_available" ? "Always available" :
                           info?.status === "live" ? `Live · ${info.user_email || ""}` :
                           info?.status === "not_connected" ? "Not connected" :
                           info?.status || "—"}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Configure */}
              <div className="space-y-4">
                <div className="overline">Configure Priority</div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="overline text-xs mb-1 block">Default (Tier 0)</label>
                    <select
                      value={dataForm.default}
                      onChange={(e) => setDataForm({ ...dataForm, default: e.target.value })}
                      className="w-full bg-[var(--bg-page)] border border-[var(--border)] text-white text-sm font-mono-data px-3 py-2 outline-none"
                    >
                      <option value="angelone">Angel One (policy primary)</option>
                      <option value="dhan">Dhan</option>
                      <option value="zerodha">Zerodha (Kite)</option>
                      <option value="tradingview">TradingView (coming soon)</option>
                      <option value="icicibreez">ICICI Breeze</option>
                      <option value="none">None (skip to fallback)</option>
                    </select>
                    <p className="text-xs txt-muted mt-1">Connect admin's Dhan account under Brokers.</p>
                  </div>
                  <div>
                    <label className="overline text-xs mb-1 block">Fallback (Tier 1)</label>
                    <select
                      value={dataForm.fallback}
                      onChange={(e) => setDataForm({ ...dataForm, fallback: e.target.value })}
                      className="w-full bg-[var(--bg-page)] border border-[var(--border)] text-white text-sm font-mono-data px-3 py-2 outline-none"
                    >
                      <option value="angelone">Angel One (policy primary)</option>
                      <option value="zerodha">Zerodha (Kite)</option>
                      <option value="dhan">Dhan</option>
                      <option value="tradingview">TradingView (coming soon)</option>
                      <option value="icicibreez">ICICI Breeze</option>
                      <option value="none">None (skip to Yahoo)</option>
                    </select>
                    <p className="text-xs txt-muted mt-1">Connect admin's Zerodha account under Brokers.</p>
                  </div>
                </div>
                <Button
                  onClick={saveDataSource}
                  disabled={dataSaving}
                  className="rounded-none bg-white text-black hover:bg-zinc-200 font-section tracking-wider"
                >
                  {dataSaving ? "SAVING…" : "SAVE DATA SOURCE CONFIG"}
                </Button>
              </div>

              <div className="border border-[var(--border)] p-4 text-xs txt-secondary space-y-1">
                <div className="overline mb-2">How it works</div>
                <div>· Admin connects Dhan account → all users get Dhan OHLCV + MCX data</div>
                <div>· If Dhan unavailable → falls to Zerodha Kite (NSE only)</div>
                <div>· If both unavailable → Yahoo Finance (free, NSE + global)</div>
                <div>· Final fallback → deterministic mock data (always works)</div>
                <div>· Traders connect only their own broker for order execution</div>
              </div>
            </div>

            {/* MCX Data Source */}
            <div className="panel p-6 space-y-6 max-w-2xl mt-4">
              <div>
                <div className="font-display text-xl mb-1">MCX Commodity Data Source</div>
                <p className="text-sm txt-secondary">
                  MCX symbols (GOLD, SILVER, CRUDEOIL, NATURALGAS, COPPER, ZINC) require a separate data source.
                  Zerodha explicitly excludes MCX. Dhan requires the Data API subscription.
                </p>
              </div>

              {mcxDataSource && (
                <div className="space-y-2">
                  <div className="overline mb-2">Active MCX Tiers</div>
                  {[
                    { label: "Default",  key: "default" },
                    { label: "Fallback", key: "fallback" },
                  ].map((row, i) => {
                    const src = mcxDataSource.default === row.key || row.key === "default"
                      ? mcxForm[row.key]
                      : mcxForm[row.key];
                    const info = mcxDataSource.sources?.[mcxForm[row.key]] || {};
                    const isActive = mcxForm[row.key] !== "none";
                    return (
                      <div key={i} className="flex items-center gap-4 border border-[var(--border)] px-4 py-3">
                        <span className={`w-2 h-2 rounded-full shrink-0 ${isActive ? "bg-emerald-500" : "bg-zinc-600"}`} />
                        <span className="overline w-20 shrink-0">{row.label}</span>
                        <span className="font-mono-data text-sm uppercase">{mcxForm[row.key]}</span>
                        <span className="text-xs txt-muted ml-auto">{info.note || ""}</span>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="space-y-4">
                <div className="overline">Configure MCX Priority</div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="overline text-xs mb-1 block">Default (Tier 0)</label>
                    <select
                      value={mcxForm.default}
                      onChange={(e) => setMcxForm({ ...mcxForm, default: e.target.value })}
                      className="w-full bg-[var(--bg-page)] border border-[var(--border)] text-white text-sm font-mono-data px-3 py-2 outline-none"
                    >
                      <option value="angelone">Angel One (policy primary)</option>
                      <option value="dhan">Dhan (Data API subscription required)</option>
                      <option value="yahoo_inr">Yahoo Finance USD→INR (free, ~95% accurate)</option>
                      <option value="tradingview">TradingView (coming soon)</option>
                      <option value="icicibreez">ICICI Breeze</option>
                      <option value="none">None</option>
                    </select>
                    <p className="text-xs txt-muted mt-1">Dhan Data API: available at dhanhq.co → API plans.</p>
                  </div>
                  <div>
                    <label className="overline text-xs mb-1 block">Fallback (Tier 1)</label>
                    <select
                      value={mcxForm.fallback}
                      onChange={(e) => setMcxForm({ ...mcxForm, fallback: e.target.value })}
                      className="w-full bg-[var(--bg-page)] border border-[var(--border)] text-white text-sm font-mono-data px-3 py-2 outline-none"
                    >
                      <option value="angelone">Angel One (policy primary)</option>
                      <option value="yahoo_inr">Yahoo Finance USD→INR (free, ~95% accurate)</option>
                      <option value="dhan">Dhan (Data API subscription required)</option>
                      <option value="tradingview">TradingView (coming soon)</option>
                      <option value="icicibreez">ICICI Breeze</option>
                      <option value="none">None (MCX shows no data)</option>
                    </select>
                    <p className="text-xs txt-muted mt-1">Yahoo converts CL=F, GC=F etc. via live USDINR rate.</p>
                  </div>
                </div>
                <Button
                  onClick={saveMcxDataSource}
                  disabled={mcxSaving}
                  className="rounded-none bg-white text-black hover:bg-zinc-200 font-section tracking-wider"
                >
                  {mcxSaving ? "SAVING…" : "SAVE MCX DATA SOURCE"}
                </Button>
              </div>
            </div>

            {/* TradingView Configuration */}
            <div className="panel p-6 space-y-6 max-w-2xl mt-4">
              <div>
                <div className="font-display text-xl mb-1">TradingView Data Source</div>
                <p className="text-sm txt-secondary">
                  Enter your TradingView account credentials. Data is fetched via the tvdatafeed community
                  library using TradingView's internal API. Works with free and Pro accounts.
                  Supports NSE equities, indices, and MCX commodities with INR prices.
                </p>
              </div>

              {tvConfig && (
                <div className="flex items-center gap-3 border border-[var(--border)] px-4 py-3">
                  <span className={`w-2 h-2 rounded-full shrink-0 ${tvConfig.configured ? "bg-emerald-500" : "bg-zinc-600"}`} />
                  <span className="text-sm">
                    {tvConfig.configured
                      ? <>Configured — <span className="font-mono-data">{tvConfig.username}</span></>
                      : "Not configured"}
                  </span>
                  {tvConfig.updated_at && (
                    <span className="text-xs txt-muted ml-auto">saved {new Date(tvConfig.updated_at).toLocaleDateString()}</span>
                  )}
                </div>
              )}

              <div className="space-y-4">
                <div className="overline">TradingView Credentials</div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="overline text-xs mb-1 block">Username / Email</label>
                    <input
                      type="text"
                      value={tvForm.username}
                      onChange={(e) => setTvForm({ ...tvForm, username: e.target.value })}
                      placeholder="your@email.com or username"
                      className="w-full bg-[var(--bg-page)] border border-[var(--border)] text-white text-sm font-mono-data px-3 py-2 outline-none"
                    />
                  </div>
                  <div>
                    <label className="overline text-xs mb-1 block">Password</label>
                    <input
                      type="password"
                      value={tvForm.password}
                      onChange={(e) => setTvForm({ ...tvForm, password: e.target.value })}
                      placeholder={tvConfig?.configured ? "Enter new password to update" : "TradingView password"}
                      className="w-full bg-[var(--bg-page)] border border-[var(--border)] text-white text-sm font-mono-data px-3 py-2 outline-none"
                    />
                  </div>
                </div>
                <p className="text-xs txt-muted">
                  Credentials stored encrypted (Fernet). Install: <span className="font-mono-data">pip install tvdatafeed</span> on the backend server.
                </p>
                <Button
                  onClick={saveTvConfig}
                  disabled={tvSaving}
                  className="rounded-none bg-white text-black hover:bg-zinc-200 font-section tracking-wider"
                >
                  {tvSaving ? "SAVING…" : tvConfig?.configured ? "UPDATE TRADINGVIEW CREDENTIALS" : "SAVE TRADINGVIEW CREDENTIALS"}
                </Button>
              </div>
            </div>
          {/* MCX Instrument Security IDs */}
            <div className="panel p-6 space-y-4 max-w-2xl mt-4">
              <div>
                <div className="font-display text-xl mb-1">MCX Instrument Security IDs</div>
                <p className="text-xs txt-muted">
                  MCX futures roll monthly — security IDs change each expiry. AlgoForge tries to
                  auto-discover these from Dhan's instrument master, but the URL sometimes blocks.
                  Paste the current front-month IDs here to unblock intraday MCX backtest.
                </p>
                <p className="text-xs txt-muted mt-1">
                  Find IDs: Dhan web portal → Instruments → search symbol → note Security ID. Or download
                  the CSV from <span className="font-mono-data">images.dhan.co/api-data/api-scrip-master.csv</span> and
                  filter for MCX + FUTCOM + nearest expiry.
                </p>
              </div>
              {mcxInstruments && (() => {
                const daysSince = mcxInstruments.updated_at
                  ? Math.floor((Date.now() - new Date(mcxInstruments.updated_at)) / 86400000)
                  : null;
                const stale = daysSince !== null && daysSince >= 25;
                return (
                  <>
                    <div className="flex items-center gap-2 text-xs txt-muted">
                      <span>Source: <span className="font-mono-data">{mcxInstruments.source || "none"}</span></span>
                      {mcxInstruments.updated_at && (
                        <span>· updated {new Date(mcxInstruments.updated_at).toLocaleDateString()}
                          {daysSince !== null && <span className={stale ? "text-amber-400 font-semibold" : ""}> ({daysSince}d ago)</span>}
                        </span>
                      )}
                    </div>
                    {stale && (
                      <div className="flex items-start gap-2 rounded-none border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-300">
                        <span className="shrink-0 font-bold">⚠</span>
                        <span>
                          MCX security IDs were last updated <strong>{daysSince} days ago</strong>.
                          Front-month contracts roll monthly — IDs may be stale. Refresh from Dhan CSV and update below.
                        </span>
                      </div>
                    )}
                  </>
                );
              })()}
              <div className="grid grid-cols-2 gap-3">
                {["GOLD","SILVER","CRUDEOIL","NATURALGAS","COPPER","ZINC"].map(sym => {
                  const info = mcxInstruments?.ids?.[sym];
                  return (
                    <div key={sym} className="space-y-1">
                      <div className="flex items-center gap-2">
                        <label className="overline text-xs w-28 shrink-0">{sym}</label>
                        {info?.active && <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" title="Active" />}
                      </div>
                      <Input
                        value={mcxIdsForm[sym] || ""}
                        onChange={e => setMcxIdsForm(f => ({ ...f, [sym]: e.target.value }))}
                        placeholder="Security ID (e.g. 428631)"
                        className="rounded-none h-8 text-sm font-mono-data bg-[var(--bg-page)] border-[var(--border)]"
                      />
                    </div>
                  );
                })}
              </div>
              <Button
                onClick={saveMcxInstruments}
                disabled={mcxIdsSaving}
                className="rounded-none bg-white text-black hover:bg-zinc-200 font-section tracking-wider"
              >
                {mcxIdsSaving ? "SAVING…" : "SAVE MCX SECURITY IDs"}
              </Button>
            </div>
          </TabsContent>

          {/* Risk / Users */}
          <TabsContent value="risk" className="mt-4">
            <div className="panel">
              <div className="flex items-center gap-3 px-4 py-3 border-b border-[var(--border)]">
                <Search className="w-4 h-4 txt-muted shrink-0" />
                <Input
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  placeholder="Search by email or name…"
                  className="rounded-none h-8 text-sm bg-[var(--bg-page)] border-[var(--border)] flex-1 max-w-sm"
                />
                <span className="text-xs txt-muted ml-auto">{users.filter(u => !userSearch || u.email.includes(userSearch) || (u.name || "").toLowerCase().includes(userSearch.toLowerCase())).length} users</span>
              </div>
              <Table>
                <TableHeader>
                  <TableRow className="border-[var(--border)]">
                    <TableHead className="overline">Email</TableHead>
                    <TableHead className="overline">Role</TableHead>
                    <TableHead className="overline text-right">Pos</TableHead>
                    <TableHead className="overline text-right">P&L</TableHead>
                    <TableHead className="overline text-right">Exposure</TableHead>
                    <TableHead className="overline">Kill</TableHead>
                    <TableHead className="overline">Tier</TableHead>
                    <TableHead className="overline">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users
                    .filter(u => !userSearch || u.email.includes(userSearch) || (u.name || "").toLowerCase().includes(userSearch.toLowerCase()))
                    .map((u) => (
                    <TableRow key={u.id} className={`border-[var(--border)] ${u.status === "suspended" ? "opacity-50" : ""}`} data-testid={`admin-user-${u.id}`}>
                      <TableCell className="font-mono-data text-xs">
                        <div>{u.email}</div>
                        {u.status && u.status !== "active" && (
                          <div className={`text-xs mt-0.5 ${u.status === "pending" ? "txt-warn" : "txt-loss"}`}>{u.status.toUpperCase()}</div>
                        )}
                      </TableCell>
                      <TableCell className={`font-section text-xs ${u.role === "admin" ? "txt-warn" : "txt-secondary"}`}>{u.role.toUpperCase()}</TableCell>
                      <TableCell className="font-mono-data text-right">{u.open_positions}</TableCell>
                      <TableCell className={`font-mono-data text-right ${u.total_pnl > 0 ? "txt-profit" : u.total_pnl < 0 ? "txt-loss" : "txt-muted"}`}>₹{fmt(u.total_pnl)}</TableCell>
                      <TableCell className="font-mono-data text-right">₹{fmt(u.exposure, 0)}</TableCell>
                      <TableCell className={`font-section text-xs ${u.kill_switch ? "txt-loss" : "txt-muted"}`}>{u.kill_switch ? "ARMED" : "—"}</TableCell>
                      <TableCell>
                        <select
                          value={u.subscription_tier || "free"}
                          onChange={(e) => setUserTier(u.id, u.email, e.target.value)}
                          className="bg-[var(--bg-page)] border border-[var(--border)] text-white text-xs font-section px-2 py-1 outline-none cursor-pointer"
                        >
                          <option value="free">FREE</option>
                          <option value="pro">PRO</option>
                          <option value="enterprise">ENTERPRISE</option>
                        </select>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 flex-wrap">
                          <select
                            data-testid={`admin-role-select-${u.id}`}
                            value={u.role}
                            disabled={promotingId === u.email}
                            onChange={(e) => promoteUser(u.email, e.target.value)}
                            className="bg-[var(--bg-page)] border border-[var(--border)] text-white text-xs font-section px-2 py-1 outline-none cursor-pointer"
                          >
                            <option value="trader">TRADER</option>
                            <option value="admin">ADMIN</option>
                            <option value="signal_user">SIGNAL USER</option>
                          </select>
                          <Button
                            data-testid={`admin-kill-${u.id}`}
                            size="sm"
                            variant="ghost"
                            onClick={() => forceKill(u.id, u.email, !u.kill_switch)}
                            className={`rounded-none ${u.kill_switch ? "text-emerald-400 hover:bg-emerald-500 hover:text-black" : "text-red-400 hover:bg-red-500 hover:text-white"}`}
                          >
                            <ShieldAlert className="w-3.5 h-3.5 mr-1" /> {u.kill_switch ? "RELEASE" : "KILL"}
                          </Button>
                          <Button
                            size="sm" variant="ghost"
                            onClick={() => toggleUserStatus(u.id, u.email, u.status)}
                            className={`rounded-none ${u.status === "suspended" ? "text-emerald-400 hover:bg-emerald-500/20" : "txt-muted hover:text-orange-400"}`}
                            title={u.status === "suspended" ? "Enable account" : "Suspend account"}
                          >
                            {u.status === "suspended"
                              ? <Power className="w-3.5 h-3.5" />
                              : <PowerOff className="w-3.5 h-3.5" />}
                          </Button>
                          <Button
                            size="sm" variant="ghost"
                            onClick={() => resetPassword(u.id, u.email)}
                            className="rounded-none txt-muted hover:text-blue-400 hover:bg-blue-500/10"
                            title="Force reset password"
                          >
                            <KeyRound className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            size="sm" variant="ghost"
                            onClick={() => resetDemo(u.id, u.email)}
                            className="rounded-none txt-muted hover:text-purple-400 hover:bg-purple-500/10"
                            title="Reset demo account"
                          >
                            <RotateCcw className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Broker map */}
          <TabsContent value="brokers" className="mt-4 space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {Object.entries(brokerMap.by_broker).map(([b, s]) => (
                <div key={b} className="panel p-4" data-testid={`admin-broker-stat-${b}`}>
                  <div className="overline">{b}</div>
                  <div className="kpi-num text-3xl mt-2">{s.total}</div>
                  <div className="text-xs font-mono-data mt-2 space-x-3">
                    <span className="txt-profit">{s.live || 0} live</span>
                    <span className="txt-warn">{s.saved || 0} saved</span>
                    <span className="txt-loss">{s.error || 0} err</span>
                  </div>
                </div>
              ))}
              {Object.keys(brokerMap.by_broker).length === 0 && (
                <div className="panel p-6 col-span-full txt-muted text-sm text-center">No broker connections across users.</div>
              )}
            </div>
            <div className="panel">
              <Table>
                <TableHeader>
                  <TableRow className="border-[var(--border)]">
                    <TableHead className="overline">User</TableHead>
                    <TableHead className="overline">Broker</TableHead>
                    <TableHead className="overline">Status</TableHead>
                    <TableHead className="overline">Last test</TableHead>
                    <TableHead className="overline">Message</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {brokerMap.connections.map((c) => (
                    <TableRow key={`${c.user_id}-${c.broker}`} className="border-[var(--border)]">
                      <TableCell className="font-mono-data text-xs">{c.user_email}</TableCell>
                      <TableCell className="font-section">{c.broker}</TableCell>
                      <TableCell className={`font-section text-xs ${c.status === "live" ? "txt-profit" : c.status === "error" ? "txt-loss" : "txt-warn"}`}>{(c.status || "saved").toUpperCase()}</TableCell>
                      <TableCell className="font-mono-data text-xs txt-secondary">{c.last_test?.slice(11, 19) || "—"}</TableCell>
                      <TableCell className="text-xs txt-secondary truncate max-w-md">{c.last_message || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Global audit feed */}
          <TabsContent value="audit" className="mt-4">
            <div className="panel">
              <Table>
                <TableHeader>
                  <TableRow className="border-[var(--border)]">
                    <TableHead className="overline">Timestamp</TableHead>
                    <TableHead className="overline">User</TableHead>
                    <TableHead className="overline">Type</TableHead>
                    <TableHead className="overline">Severity</TableHead>
                    <TableHead className="overline">Summary</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {events.map((e) => (
                    <TableRow key={e.id} className="border-[var(--border)]">
                      <TableCell className="font-mono-data text-xs txt-secondary">{e.ts?.slice(0, 19)}</TableCell>
                      <TableCell className="font-mono-data text-xs txt-muted">{(e.user_id || "—").slice(0, 8)}</TableCell>
                      <TableCell className="font-section text-xs">{e.event_type}</TableCell>
                      <TableCell className={`font-section text-xs ${e.severity === "HIGH" ? "txt-loss" : e.severity === "WARN" ? "txt-warn" : "txt-secondary"}`}>{e.severity}</TableCell>
                      <TableCell className="text-sm">{e.summary}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Admin trail */}
          <TabsContent value="events" className="mt-4">
            <div className="panel">
              <Table>
                <TableHeader>
                  <TableRow className="border-[var(--border)]">
                    <TableHead className="overline">Timestamp</TableHead>
                    <TableHead className="overline">Admin</TableHead>
                    <TableHead className="overline">Action</TableHead>
                    <TableHead className="overline">Target</TableHead>
                    <TableHead className="overline">Summary</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {adminEvents.length === 0 && (
                    <TableRow className="border-[var(--border)]"><TableCell colSpan={5} className="txt-muted text-center py-6">No admin actions yet.</TableCell></TableRow>
                  )}
                  {adminEvents.map((e) => (
                    <TableRow key={e.id} className="border-[var(--border)]">
                      <TableCell className="font-mono-data text-xs txt-secondary">{e.ts?.slice(0, 19)}</TableCell>
                      <TableCell className="font-mono-data text-xs">{e.admin_id?.slice(0, 8)}</TableCell>
                      <TableCell className="font-section text-xs txt-warn">{e.action}</TableCell>
                      <TableCell className="font-mono-data text-xs">{(e.target_user_id || e.target_broker || "—").slice(0, 12)}</TableCell>
                      <TableCell className="text-sm">{e.summary}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Feature Flags */}
          <TabsContent value="features" className="mt-4">
            <FeatureFlagsPanel />
          </TabsContent>

          {/* Platform Config */}
          <TabsContent value="platform" className="mt-4">
            <div className="panel p-6 max-w-lg space-y-6">
              <div>
                <div className="font-display text-xl mb-1">Platform Configuration</div>
                <p className="text-sm txt-secondary">These settings take effect immediately — no restart required.</p>
              </div>
              <div>
                <div className="overline mb-3">Signal Scanner Interval</div>
                <div className="flex-1">
                  <label className="text-xs txt-muted block mb-1">Interval (minutes)</label>
                  <input
                    type="number"
                    min={1} max={240}
                    value={scanInterval}
                    onChange={(e) => setScanInterval(e.target.value)}
                    className="w-full bg-[var(--bg-page)] border border-[var(--border)] text-white font-mono-data px-3 py-2 outline-none text-sm"
                  />
                  <p className="text-xs txt-muted mt-1">Default: 30 min. Range: 1–240 min. Only runs during market hours (09:15–15:30 IST).</p>
                </div>
              </div>

              {/* Auto Paper-Trade Trap Signals */}
              <div className="border border-[var(--border)] p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-section text-sm">Auto Paper-Trade Trap Signals</div>
                    <div className="text-xs txt-muted mt-0.5">
                      When a trap signal fires and passes the quality gate, automatically save
                      the strategy and place a paper trade for the admin account — no manual click needed.
                    </div>
                  </div>
                  <button
                    onClick={() => setAutoPaperTrap(v => !v)}
                    className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ml-4 ${
                      autoPaperTrap ? "bg-emerald-500" : "bg-zinc-600"
                    }`}
                  >
                    <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${
                      autoPaperTrap ? "translate-x-5" : "translate-x-0"
                    }`} />
                  </button>
                </div>

                {autoPaperTrap && (
                  <div>
                    <label className="text-xs txt-muted block mb-1">
                      Minimum confidence to auto-execute ({trapConfMin}%)
                    </label>
                    <input
                      type="range"
                      min={50} max={95} step={5}
                      value={trapConfMin}
                      onChange={(e) => setTrapConfMin(Number(e.target.value))}
                      className="w-full accent-emerald-500"
                    />
                    <div className="flex justify-between text-xs txt-muted mt-1">
                      <span>50% (more trades)</span>
                      <span>95% (fewer, higher quality)</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Account Registration Approval */}
              <div className="border border-[var(--border)] p-4 space-y-3">
                <div>
                  <div className="font-section text-sm">Account Registration Approval</div>
                  <div className="text-xs txt-muted mt-0.5">
                    When enabled, new sign-ups are placed in a pending queue and require admin approval before they can log in.
                    You receive a Telegram/email alert on every new registration.
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <span className={`text-xs font-section tracking-wider px-2 py-0.5 border ${requireApproval ? "border-amber-500 text-amber-400" : "border-zinc-600 txt-muted"}`}>
                      {requireApproval ? "MANUAL APPROVAL" : "AUTO-APPROVE"}
                    </span>
                  </div>
                  <button
                    onClick={() => setRequireApproval(v => !v)}
                    className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${requireApproval ? "bg-amber-500" : "bg-zinc-600"}`}
                  >
                    <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${requireApproval ? "translate-x-5" : "translate-x-0"}`} />
                  </button>
                </div>
                {requireApproval && (
                  <p className="text-xs txt-warn">
                    ⚠ New registrations will be held in Pending Users tab until approved. Admin is notified via Telegram/email on each sign-up.
                  </p>
                )}
              </div>

              {/* Strategy Prompt Max Length */}
              <div className="border border-[var(--border)] p-4 space-y-3">
                <div>
                  <div className="font-section text-sm">Strategy Prompt Max Length</div>
                  <div className="text-xs txt-muted mt-0.5">
                    Maximum characters a user can send to the AI Strategy Builder. Higher values allow more detailed strategies but cost more tokens.
                  </div>
                </div>
                <div>
                  <label className="text-xs txt-muted block mb-1">
                    Max characters: <span className="text-white font-mono-data">{stratPromptMax.toLocaleString()}</span>
                  </label>
                  <input
                    type="range"
                    min={500} max={10000} step={500}
                    value={stratPromptMax}
                    onChange={(e) => setStratPromptMax(Number(e.target.value))}
                    className="w-full accent-emerald-500"
                  />
                  <div className="flex justify-between text-xs txt-muted mt-1">
                    <span>500 (brief)</span>
                    <span>10,000 (detailed)</span>
                  </div>
                </div>
              </div>

              <Button
                onClick={savePlatformConfig}
                disabled={savingCfg}
                className="rounded-none bg-white text-black hover:bg-zinc-200 font-section tracking-wider h-10 w-full"
              >
                {savingCfg ? "SAVING…" : "SAVE PLATFORM CONFIG"}
              </Button>
            </div>
          </TabsContent>

          {/* Pending Users */}
          <TabsContent value="pending" className="mt-4">
            <div className="panel">
              {pendingUsers.length === 0 ? (
                <div className="p-8 text-center txt-muted text-sm">No pending registrations. Enable "Manual Approval" in Platform Settings to hold new sign-ups here.</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-[var(--border)]">
                      <TableHead className="overline">Email</TableHead>
                      <TableHead className="overline">Name</TableHead>
                      <TableHead className="overline">Registered</TableHead>
                      <TableHead className="overline">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingUsers.map((u) => (
                      <TableRow key={u.id} className="border-[var(--border)]">
                        <TableCell className="font-mono-data text-xs">{u.email}</TableCell>
                        <TableCell className="text-sm">{u.name || "—"}</TableCell>
                        <TableCell className="font-mono-data text-xs txt-secondary">{u.created_at?.slice(0, 10)}</TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              disabled={approvingId === u.id}
                              onClick={() => approveUser(u.id, u.email)}
                              className="rounded-none bg-emerald-600 hover:bg-emerald-500 text-white font-section text-xs"
                            >
                              <UserCheck className="w-3 h-3 mr-1" /> APPROVE
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => api.post(`/admin/users/${u.id}/suspend`).then(() => { toast.success("Rejected"); load(); })}
                              className="rounded-none text-red-400 hover:bg-red-500/20 font-section text-xs"
                            >
                              REJECT
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </TabsContent>

          {/* Promotion Requests */}
          <TabsContent value="promotions" className="mt-4">
            <div className="panel">
              {promotionReqs.length === 0 ? (
                <div className="p-8 text-center txt-muted text-sm">No pending promotion requests. Signal Users can request Trader access from Settings → Security.</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-[var(--border)]">
                      <TableHead className="overline">Email</TableHead>
                      <TableHead className="overline">Name</TableHead>
                      <TableHead className="overline">Current Role</TableHead>
                      <TableHead className="overline">Reason</TableHead>
                      <TableHead className="overline">Submitted</TableHead>
                      <TableHead className="overline">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {promotionReqs.map((r) => (
                      <TableRow key={r.id} className="border-[var(--border)]">
                        <TableCell className="font-mono-data text-xs">{r.user_email}</TableCell>
                        <TableCell className="text-sm">{r.user_name || "—"}</TableCell>
                        <TableCell className="font-section text-xs txt-warn">{r.current_role?.toUpperCase()}</TableCell>
                        <TableCell className="text-xs txt-secondary max-w-xs truncate">{r.reason || "—"}</TableCell>
                        <TableCell className="font-mono-data text-xs txt-secondary">{r.created_at?.slice(0, 10)}</TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              disabled={approvingId === r.id}
                              onClick={() => approvePromotion(r.id, r.user_email)}
                              className="rounded-none bg-blue-600 hover:bg-blue-500 text-white font-section text-xs"
                            >
                              <TrendingUp className="w-3 h-3 mr-1" /> APPROVE
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => rejectPromotion(r.id)}
                              className="rounded-none text-red-400 hover:bg-red-500/20 font-section text-xs"
                            >
                              REJECT
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </TabsContent>

          {/* Platform Settings */}
          <TabsContent value="platform-settings" className="mt-4 space-y-4">
            {/* Helper components */}
            {(() => {
              const Field = ({ label, field, type = "text", placeholder = "" }) => (
                <div>
                  <label className="text-xs txt-muted block mb-1">{label}</label>
                  <input
                    type={type}
                    value={extCfg[field] ?? ""}
                    onChange={(e) => setExtCfg(prev => ({ ...prev, [field]: type === "number" ? Number(e.target.value) : e.target.value }))}
                    placeholder={placeholder}
                    className="w-full bg-[var(--bg-page)] border border-[var(--border)] text-[var(--text-primary)] text-sm font-mono-data px-3 py-2 outline-none focus:border-[var(--accent)]"
                  />
                </div>
              );
              const Section = ({ icon: Icon, title, desc, children, section, fields }) => (
                <div className="panel p-6 space-y-4">
                  <div className="flex items-start gap-3">
                    <Icon className="w-5 h-5 txt-accent mt-0.5 shrink-0" />
                    <div>
                      <div className="font-section text-sm tracking-wider">{title}</div>
                      <div className="text-xs txt-muted mt-0.5">{desc}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
                  <Button
                    onClick={() => saveExtCfg(section, fields)}
                    disabled={savingExtCfg[section]}
                    className="rounded-none bg-[var(--accent)] hover:bg-indigo-500 text-white font-section tracking-wider text-xs h-8"
                  >
                    <Save className="w-3 h-3 mr-1.5" />
                    {savingExtCfg[section] ? "SAVING…" : `SAVE ${title.toUpperCase()}`}
                  </Button>
                </div>
              );
              return (
                <>
                  <Section icon={CreditCard} title="Payment Gateway" desc="Razorpay (India) or Stripe (global). Only one needs to be configured."
                    section="payment" fields={["razorpay_key_id","razorpay_key_secret","stripe_publishable_key","stripe_secret_key"]}>
                    <Field label="Razorpay Key ID" field="razorpay_key_id" placeholder="rzp_live_…" />
                    <Field label="Razorpay Key Secret" field="razorpay_key_secret" type="password" placeholder="••••••" />
                    <Field label="Stripe Publishable Key" field="stripe_publishable_key" placeholder="pk_live_…" />
                    <Field label="Stripe Secret Key" field="stripe_secret_key" type="password" placeholder="sk_live_…" />
                  </Section>

                  <Section icon={Share2} title="Social Media" desc="Links used in footer, blog, and email templates."
                    section="social" fields={["twitter_url","linkedin_url","telegram_url","youtube_url","instagram_url","support_email","app_url"]}>
                    <Field label="Twitter / X URL" field="twitter_url" placeholder="https://x.com/algoforge" />
                    <Field label="LinkedIn URL" field="linkedin_url" placeholder="https://linkedin.com/company/…" />
                    <Field label="Telegram URL" field="telegram_url" placeholder="https://t.me/algoforge" />
                    <Field label="YouTube URL" field="youtube_url" placeholder="https://youtube.com/@algoforge" />
                    <Field label="Instagram URL" field="instagram_url" placeholder="https://instagram.com/algoforge" />
                    <Field label="Support Email" field="support_email" placeholder="support@algoforge.tech" />
                    <Field label="App URL" field="app_url" placeholder="https://algoforge.tech" />
                  </Section>

                  <Section icon={Bot} title="AI Agent Instructions" desc="Control what the platform AI agent posts, how often, and what topics to avoid. These instructions are injected into every agent run."
                    section="ai" fields={["agent_post_frequency","agent_post_topics","agent_avoid_topics","agent_tone","agent_custom_instructions","agent_max_posts_per_day","agent_post_time_window"]}>
                    <div>
                      <label className="text-xs txt-muted block mb-1">Post Frequency</label>
                      <select
                        value={extCfg.agent_post_frequency ?? "daily"}
                        onChange={(e) => setExtCfg(prev => ({ ...prev, agent_post_frequency: e.target.value }))}
                        className="w-full bg-[var(--bg-page)] border border-[var(--border)] text-[var(--text-primary)] text-sm font-mono-data px-3 py-2 outline-none"
                      >
                        <option value="realtime">Real-time (on every signal)</option>
                        <option value="hourly">Hourly digest</option>
                        <option value="daily">Daily summary</option>
                        <option value="weekly">Weekly recap</option>
                        <option value="manual">Manual only</option>
                      </select>
                    </div>
                    <Field label="Max Posts Per Day" field="agent_max_posts_per_day" type="number" placeholder="5" />
                    <Field label="Active Posting Window (IST)" field="agent_post_time_window" placeholder="09:15–15:30" />
                    <div>
                      <label className="text-xs txt-muted block mb-1">Agent Tone / Persona</label>
                      <select
                        value={extCfg.agent_tone ?? "professional"}
                        onChange={(e) => setExtCfg(prev => ({ ...prev, agent_tone: e.target.value }))}
                        className="w-full bg-[var(--bg-page)] border border-[var(--border)] text-[var(--text-primary)] text-sm font-mono-data px-3 py-2 outline-none"
                      >
                        <option value="professional">Professional / Bloomberg-style</option>
                        <option value="educational">Educational / beginner-friendly</option>
                        <option value="concise">Concise / data-first</option>
                        <option value="conversational">Conversational</option>
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-xs txt-muted block mb-1">What to Post (topics the agent should cover)</label>
                      <textarea
                        value={extCfg.agent_post_topics ?? ""}
                        onChange={(e) => setExtCfg(prev => ({ ...prev, agent_post_topics: e.target.value }))}
                        rows={3}
                        placeholder={"Active trade signals with entry/SL/target\nMarket open & close summaries\nHigh-confidence trap detections\nWeekly P&L recaps"}
                        className="w-full bg-[var(--bg-page)] border border-[var(--border)] text-[var(--text-primary)] text-sm font-mono-data px-3 py-2 outline-none focus:border-[var(--accent)] resize-none"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-xs txt-muted block mb-1">What NOT to Post (topics to strictly avoid)</label>
                      <textarea
                        value={extCfg.agent_avoid_topics ?? ""}
                        onChange={(e) => setExtCfg(prev => ({ ...prev, agent_avoid_topics: e.target.value }))}
                        rows={3}
                        placeholder={"Specific buy/sell recommendations without disclaimers\nGuaranteed returns or profit claims\nPolitical commentary\nCompetitor comparisons"}
                        className="w-full bg-[var(--bg-page)] border border-[var(--border)] text-[var(--text-primary)] text-sm font-mono-data px-3 py-2 outline-none focus:border-[var(--accent)] resize-none"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-xs txt-muted block mb-1">Custom Instructions (appended to every agent prompt)</label>
                      <textarea
                        value={extCfg.agent_custom_instructions ?? ""}
                        onChange={(e) => setExtCfg(prev => ({ ...prev, agent_custom_instructions: e.target.value }))}
                        rows={4}
                        placeholder={"Always add SEBI disclaimer: 'Not SEBI registered. For educational purposes only.'\nUse ₹ for Indian Rupee amounts.\nKeep posts under 280 characters for Twitter."}
                        className="w-full bg-[var(--bg-page)] border border-[var(--border)] text-[var(--text-primary)] text-sm font-mono-data px-3 py-2 outline-none focus:border-[var(--accent)] resize-none"
                      />
                    </div>
                  </Section>

                  <Section icon={Globe} title="SEO & Analytics" desc="Site metadata, Open Graph, and analytics tracking codes."
                    section="seo" fields={["seo_site_name","seo_tagline","seo_description","seo_og_image_url","google_analytics_id","google_search_console"]}>
                    <Field label="Site Name" field="seo_site_name" placeholder="AlgoForge" />
                    <Field label="Tagline" field="seo_tagline" placeholder="Algorithmic trading for Indian markets" />
                    <div className="md:col-span-2">
                      <label className="text-xs txt-muted block mb-1">Meta Description</label>
                      <textarea
                        value={extCfg.seo_description ?? ""}
                        onChange={(e) => setExtCfg(prev => ({ ...prev, seo_description: e.target.value }))}
                        rows={3}
                        placeholder="AlgoForge is an algorithmic trading platform…"
                        className="w-full bg-[var(--bg-page)] border border-[var(--border)] text-[var(--text-primary)] text-sm font-mono-data px-3 py-2 outline-none focus:border-[var(--accent)] resize-none"
                      />
                    </div>
                    <Field label="OG Image URL" field="seo_og_image_url" placeholder="https://algoforge.tech/og.png" />
                    <Field label="Google Analytics ID" field="google_analytics_id" placeholder="G-XXXXXXXXXX" />
                    <Field label="Google Search Console" field="google_search_console" placeholder="Verification meta tag content" />
                  </Section>

                  <Section icon={Settings2} title="Email / SMTP" desc="Used for welcome emails, alerts, and notifications."
                    section="smtp" fields={["smtp_host","smtp_port","smtp_user","smtp_pass","smtp_from"]}>
                    <Field label="SMTP Host" field="smtp_host" placeholder="smtp.gmail.com" />
                    <Field label="SMTP Port" field="smtp_port" type="number" placeholder="587" />
                    <Field label="SMTP Username" field="smtp_user" placeholder="alerts@algoforge.tech" />
                    <Field label="SMTP Password" field="smtp_pass" type="password" placeholder="••••••" />
                    <Field label="From Address" field="smtp_from" placeholder="AlgoForge <alerts@algoforge.tech>" />
                  </Section>
                </>
              );
            })()}
          </TabsContent>

          {/* Strategies Library */}
          <TabsContent value="strategies" className="mt-4">
            <AdminStrategiesPanel />
          </TabsContent>
        </Tabs>
      </div>
    </AppShell>
  );
}

export function AdminStrategiesPanel() {   // exported for its RU-STRATLINK SL-12 test
  const [strategies, setStrategies] = useState([]);
  const [total, setTotal]           = useState(0);
  const [owner, setOwner]           = useState("all");
  const [search, setSearch]         = useState("");
  const [busy, setBusy]             = useState(false);
  const [pulling, setPulling]       = useState(null);
  const [selected, setSelected]     = useState(new Set());
  const [bulkPulling, setBulkPulling] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    setBusy(true);
    setSelected(new Set());
    try {
      const r = await api.get("/admin/strategies", { params: { owner, search, limit: 200 } });
      setStrategies(r.data.strategies || []);
      setTotal(r.data.total || 0);
    } catch { toast.error("Could not load strategies"); }
    finally { setBusy(false); }
  };

  useEffect(() => { load(); }, [owner]);

  const pull = async (sid, name) => {
    setPulling(sid);
    try {
      await api.post(`/admin/strategies/${sid}/clone`);
      toast.success(`"${name}" pulled to your Strategy Builder`);
      navigate("/strategies");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Pull failed");
    } finally {
      setPulling(null);
    }
  };

  const pullSelected = async () => {
    if (!selected.size) return;
    setBulkPulling(true);
    let ok = 0;
    let fail = 0;
    for (const sid of selected) {
      try {
        await api.post(`/admin/strategies/${sid}/clone`);
        ok++;
      } catch {
        fail++;
      }
    }
    setBulkPulling(false);
    setSelected(new Set());
    if (ok) toast.success(`${ok} ${ok === 1 ? "strategy" : "strategies"} pulled to Strategy Builder`);
    if (fail) toast.error(`${fail} failed to pull`);
    if (ok) navigate("/strategies");
  };

  const toggleSelect = (sid) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(sid) ? next.delete(sid) : next.add(sid);
      return next;
    });
  };

  const FILTER_BTNS = [
    { key: "all",   label: "ALL" },
    { key: "admin", label: "ADMIN" },
    { key: "users", label: "USERS" },
  ];

  const DEPLOY_COLOR = { live: "txt-profit", paper: "txt-warn", paused: "txt-muted" };

  const filtered = search
    ? strategies.filter(s =>
        s.name?.toLowerCase().includes(search.toLowerCase()) ||
        s.dsl?.symbol?.toLowerCase().includes(search.toLowerCase()) ||
        s.owner?.email?.toLowerCase().includes(search.toLowerCase())
      )
    : strategies;

  const allFilteredIds = filtered.map(s => s.id || s._id);
  const allSelected = allFilteredIds.length > 0 && allFilteredIds.every(id => selected.has(id));

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelected(prev => {
        const next = new Set(prev);
        allFilteredIds.forEach(id => next.delete(id));
        return next;
      });
    } else {
      setSelected(prev => {
        const next = new Set(prev);
        allFilteredIds.forEach(id => next.add(id));
        return next;
      });
    }
  };

  return (
    <div className="panel p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <BookOpen className="w-4 h-4 txt-warn" />
          <div className="overline">Strategy Library</div>
          <span className="text-xs txt-muted ml-2">{total} total</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {FILTER_BTNS.map(f => (
            <button
              key={f.key}
              onClick={() => setOwner(f.key)}
              className={`px-4 py-1.5 text-xs font-section tracking-wider border transition-colors ${
                owner === f.key
                  ? "bg-white text-black border-white"
                  : "bg-transparent text-zinc-400 border-[var(--border)] hover:text-white hover:border-zinc-500"
              }`}
            >
              {f.label}
            </button>
          ))}
          <div className="relative ml-2">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 txt-muted" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === "Enter" && load()}
              placeholder="name / symbol / email"
              className="pl-7 pr-3 py-1.5 text-xs bg-[var(--bg-page)] border border-[var(--border)] text-[var(--text-primary)] font-mono-data outline-none focus:border-[var(--accent)] w-52"
            />
          </div>
          <button onClick={load} disabled={busy} className="p-1.5 border border-[var(--border)] txt-muted hover:text-white">
            <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} />
          </button>
          {selected.size > 0 && (
            <button
              onClick={pullSelected}
              disabled={bulkPulling}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-section tracking-wider border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)] hover:text-black transition-colors disabled:opacity-40"
            >
              <Download className="w-3.5 h-3.5" />
              {bulkPulling ? "PULLING…" : `PULL SELECTED (${selected.size})`}
            </button>
          )}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="txt-muted text-sm py-8 text-center">No strategies found.</div>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-[var(--border)]">
                <TableHead className="w-8">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleSelectAll}
                    className="w-3.5 h-3.5 accent-[var(--accent)] cursor-pointer"
                    title="Select all"
                  />
                </TableHead>
                <TableHead className="font-section tracking-wider text-xs">STRATEGY</TableHead>
                <TableHead className="font-section tracking-wider text-xs">OWNER</TableHead>
                <TableHead className="font-section tracking-wider text-xs">ROLE</TableHead>
                <TableHead className="font-section tracking-wider text-xs">SYMBOL</TableHead>
                <TableHead className="font-section tracking-wider text-xs">TF</TableHead>
                <TableHead className="font-section tracking-wider text-xs">MODE</TableHead>
                <TableHead className="font-section tracking-wider text-xs">UPDATED</TableHead>
                <TableHead className="font-section tracking-wider text-xs"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((s) => {
                const mode = s.deployment?.enabled
                  ? s.deployment?.mode === "live" ? "live" : "paper"
                  : "paused";
                const sid = s.id || s._id;
                const isSelected = selected.has(sid);
                return (
                  <TableRow
                    key={sid}
                    className={`border-[var(--border)] hover:bg-[var(--bg-surface)] cursor-pointer ${isSelected ? "bg-[var(--bg-surface)]" : ""}`}
                    onClick={() => toggleSelect(sid)}
                  >
                    <TableCell onClick={e => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelect(sid)}
                        className="w-3.5 h-3.5 accent-[var(--accent)] cursor-pointer"
                      />
                    </TableCell>
                    <TableCell className="font-section tracking-wider text-sm">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <StrategyNameButton id={sid} name={s.name} testid={`admin-strategy-name-${sid}`}>{s.name || "—"}</StrategyNameButton>
                        <StrategyRefChip id={sid} testid={`admin-strategy-ref-${sid}`} />
                        <StrategyStatusChip src={s} testid={`admin-strategy-status-${sid}`} />
                      </div>
                    </TableCell>
                    <TableCell className="font-mono-data text-xs txt-muted">{s.owner?.email || s.user_id?.slice(0, 8)}</TableCell>
                    <TableCell>
                      <span className={`text-xs font-section tracking-wider ${s.owner?.role === "admin" ? "txt-warn" : "txt-muted"}`}>
                        {(s.owner?.role || "trader").toUpperCase()}
                      </span>
                    </TableCell>
                    <TableCell className="font-mono-data text-xs">{s.dsl?.symbol || "—"}</TableCell>
                    <TableCell className="font-mono-data text-xs txt-muted">{s.dsl?.timeframe || "—"}</TableCell>
                    <TableCell>
                      <span className={`text-xs font-section tracking-wider ${DEPLOY_COLOR[mode] || "txt-muted"}`}>
                        ● {mode.toUpperCase()}
                      </span>
                    </TableCell>
                    <TableCell className="font-mono-data text-xs txt-muted">
                      {s.updated_at?.slice(0, 10) || "—"}
                    </TableCell>
                    <TableCell onClick={e => e.stopPropagation()}>
                      <button
                        onClick={() => pull(sid, s.name)}
                        disabled={pulling === sid || bulkPulling}
                        title="Pull to my Strategy Builder"
                        className="flex items-center gap-1 px-2 py-1 text-xs font-section tracking-wider border border-[var(--border)] txt-muted hover:text-white hover:border-zinc-400 transition-colors disabled:opacity-40"
                      >
                        <Download className="w-3 h-3" />
                        {pulling === sid ? "…" : "PULL"}
                      </button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
